import type { IParsedProject } from "../types";

interface ISiemConfigAccessProps {
  isOwner: boolean;
  projects: IParsedProject[];
}

export interface ISiemConfigAccess {
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
}

// NOTE: NSAI-6213 — who may do what to an organization's SIEM export settings.
// Managing SIEM used to require organization ownership, which meant a tenant
// could only hand the audit destination to someone it was also willing to give
// billing, members and SAML to. It is now an RBAC family, and this is the one
// place either client decides what to offer.
//
// Shared rather than duplicated per platform because web and mobile must agree:
// if they drifted, one of them would render a screen whose request the server
// answers with a 403, or hide one it would have served. The server is always
// the authority — this only decides what the UI offers.
//
// Owners pass on all three, exactly as the server grants them, so no tenant has
// to grant its owner a role to keep the access it already had.
//
// "on at least one project" matches the server's organization-scoped check: a
// member's role is held per project, and holding the level in any project of
// the organization authorizes the organization-level route.
export const getSiemConfigAccess = ({
  isOwner,
  projects,
}: ISiemConfigAccessProps): ISiemConfigAccess => {
  if (isOwner) {
    return { canRead: true, canWrite: true, canDelete: true };
  }

  // NOTE: Read from the server-parsed flags rather than the raw permission
  // strings: the server has already applied the ladder there (a delete holder
  // reads as canRead), so the levels cannot drift apart between the two sides.
  return {
    canRead: projects.some((project) => project.permissions.canReadSiemConfig),
    canWrite: projects.some(
      (project) => project.permissions.canWriteSiemConfig,
    ),
    canDelete: projects.some(
      (project) => project.permissions.canDeleteSiemConfig,
    ),
  };
};
